/**
 * GlassTooltip — glass-dark tooltip powered by the glitchOS palette.
 *
 * Accepts any GlitchColor from lib/featureColors OR a raw { hex } for
 * one-off custom colors. Derives all styling (text, bg, border) from the
 * hex at runtime — no hardcoded Tailwind map needed.
 *
 * Desktop: hover to show, mouseLeave to hide.
 * Mobile (coarse pointer): TAP to open, stays until tap-outside or re-tap.
 */

import { useState, useRef, useEffect, useMemo } from "react"
import { createPortal } from "react-dom"
import type { ReactNode } from "react"
import { GLITCH_PALETTE, type GlitchColor } from "../lib/featureColors"
import { useCoarsePointer } from "../lib/useCoarsePointer"

// Legacy string union — covers all named glitchOS colors + arbitrary strings.
export type TooltipColor = GlitchColor | (string & {})

// Accept named colors, raw hex escape, or any legacy string value.
export type TooltipColorProp = TooltipColor | { hex: string }

function resolveHex(color: TooltipColorProp): string {
  if (typeof color === "object") return color.hex
  return GLITCH_PALETTE.find((p) => p.name === color)?.hex ?? "#ffffff"
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "")
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ]
}

interface GlassTooltipProps {
  label: string
  icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>
  explainer: string | ReactNode
  color: TooltipColorProp
  children: ReactNode
  align?: "center" | "left" | "right"
  onOpen?: () => void
  onClose?: () => void
}

let activeHide: (() => void) | null = null

export function GlassTooltip({ label, icon: Icon, explainer, color, children, align = "center", onOpen, onClose }: GlassTooltipProps) {
  const [visible, setVisible] = useState(false)
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number } | null>(null)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tooltipRef = useRef<HTMLSpanElement>(null)
  const isCoarse = useCoarsePointer()
  const movedRef = useRef(false)

  const hex = resolveHex(color)
  const [r, g, b] = useMemo(() => hexToRgb(hex), [hex])

  const styles = useMemo(() => ({
    text: hex,
    headBg: `rgba(${r},${g},${b},0.10)`,
    border: `rgba(${r},${g},${b},0.20)`,
  }), [hex, r, g, b])

  const calcPos = (measuredH?: number) => {
    if (!triggerRef.current) return null
    const rect = triggerRef.current.getBoundingClientRect()
    const tooltipH = measuredH ?? 80
    const tooltipW = 224
    const gap = 8
    const pad = 8
    const vw = window.innerWidth

    let top = rect.top - tooltipH - gap
    if (top < pad) top = rect.bottom + gap

    if (align === "right") {
      let right = vw - rect.right
      if (right < pad) right = pad
      if (vw - right - tooltipW < pad) right = vw - tooltipW - pad
      return { top, right: Math.max(pad, right) }
    }
    if (align === "left") {
      let left = rect.left
      if (left < pad) left = pad
      if (left + tooltipW > vw - pad) left = vw - tooltipW - pad
      return { top, left: Math.max(pad, left) }
    }
    let left = rect.left + rect.width / 2
    const halfW = tooltipW / 2
    if (left - halfW < pad) left = pad + halfW
    if (left + halfW > vw - pad) left = vw - pad - halfW
    return { top, left }
  }

  const show = () => {
    if (activeHide && activeHide !== hide) activeHide()
    const p = calcPos()
    if (!p) return
    setPos(p)
    setVisible(true)
    activeHide = hide
    onOpen?.()
  }

  useEffect(() => {
    if (!visible || !tooltipRef.current || !triggerRef.current) return
    const measured = tooltipRef.current.getBoundingClientRect().height
    const corrected = calcPos(measured)
    if (corrected) setPos(corrected)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  const hide = () => {
    setVisible(false)
    if (activeHide === hide) activeHide = null
    onClose?.()
  }

  const toggle = () => { visible ? hide() : show() }

  useEffect(() => {
    if (!visible || !isCoarse) return
    const dismiss = (e: TouchEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return
      hide()
    }
    document.addEventListener("touchstart", dismiss, { passive: true })
    return () => document.removeEventListener("touchstart", dismiss)
  })

  useEffect(() => () => { if (activeHide === hide) activeHide = null })

  const tooltipEl = visible && pos && (
    <span
      ref={tooltipRef}
      className="fixed z-[10010] w-max max-w-56 rounded-xl bg-black/90 backdrop-blur-xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 pointer-events-none"
      style={{
        top: pos.top,
        border: `1px solid rgba(255,255,255,0.08)`,
        boxShadow: `0 8px 32px -8px rgba(0,0,0,0.8), 0 0 0 1px ${styles.border}`,
        ...(pos.left != null && { left: pos.left, transform: align === "center" ? "translateX(-50%)" : undefined }),
        ...(pos.right != null && { right: pos.right }),
      }}
      role="tooltip"
    >
      <span
        className="flex items-center gap-1.5 px-2.5 py-1.5"
        style={{ backgroundColor: styles.headBg, borderBottom: `1px solid ${styles.border}` }}
      >
        {Icon && <Icon className="w-3 h-3 shrink-0" style={{ color: styles.text }} />}
        <span
          className="text-[9px] font-bold uppercase tracking-[0.14em] whitespace-nowrap"
          style={{ color: styles.text }}
        >
          {label}
        </span>
      </span>
      <span className="block px-2.5 py-2 text-[11px] text-white/60 leading-snug">
        {explainer}
      </span>
    </span>
  )

  const handleTouchStart = () => { movedRef.current = false }
  const handleTouchMove = () => { movedRef.current = true }
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (movedRef.current) return
    e.preventDefault()
    toggle()
  }

  return (
    <span
      ref={triggerRef}
      className="relative inline touch-manipulation select-none"
      onMouseEnter={!isCoarse ? show : undefined}
      onMouseLeave={!isCoarse ? hide : undefined}
      onTouchStart={isCoarse ? handleTouchStart : undefined}
      onTouchMove={isCoarse ? handleTouchMove : undefined}
      onTouchEnd={isCoarse ? handleTouchEnd : undefined}
    >
      {children}
      {typeof document !== "undefined" && tooltipEl && createPortal(tooltipEl, document.body)}
    </span>
  )
}
